// Developer Platform (tenant admin) — one tabbed surface for the three
// developer/integration capabilities (docs/developer-platform/architecture.md):
//   1. API clients   — OAuth client-credentials for the public API (auth-service)
//   2. Webhooks      — signed event deliveries (event-processor)
//   3. Integrations  — connector connections + sync runs (migration-service)
// Gated like Lifecycle Packages: tenant owner / company-settings admins.
// ?tab= deep-links each tab (same pattern as the Finance hub).
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { AppWindow, BarChart3, Bot, Code2, KeyRound, Plug, ShieldAlert, Store, Terminal, Webhook } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/features/shared/components/ui/tabs';
import { useHasPermission } from '@/rbac/useRbac';
import { useRoleAccess } from '@/features/hooks/useRoleAccess';
import { ApiClientsTab } from './developer/ApiClientsTab';
import { ApplicationsTab } from './developer/ApplicationsTab';
import { UsageTab } from './developer/UsageTab';
import { WebhooksTab } from './developer/WebhooksTab';
import { IntegrationsTab } from './developer/IntegrationsTab';
import { MarketplaceTab } from './developer/MarketplaceTab';
import { RetryScope } from './developer/common';
import { McpTab } from './developer/McpTab';
import { CliTab } from './developer/CliTab';

const TAB_IDS = ['clients', 'applications', 'usage', 'webhooks', 'integrations', 'marketplace', 'mcp', 'cli'] as const;
type TabId = (typeof TAB_IDS)[number];

export const DeveloperPage: React.FC = () => {
  const { t } = useTranslation('admin');
  const { isTenantOwner } = useRoleAccess();
  const canCompanySettings = useHasPermission('tenant:update:own');
  const canManage = isTenantOwner || canCompanySettings;

  // Tab state lives in the URL (?tab=…) so links can deep-link a tab.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab') as TabId | null;
  const tab: TabId = requested && TAB_IDS.includes(requested) ? requested : 'clients';
  const setTab = (v: string) => setSearchParams({ tab: v }, { replace: true });

  if (!canManage) {
    return (
      <div className="max-w-md mx-auto mt-16 text-center">
        <ShieldAlert className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
        <h1 className="text-h3 text-charcoal">
          {t('developer.admins_only', { defaultValue: 'Company admins only' })}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('developer.no_permission', { defaultValue: 'You do not have permission to manage the developer platform.' })}
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-h2 text-charcoal flex items-center gap-2">
          <Code2 className="w-6 h-6" /> {t('developer.title', { defaultValue: 'Developer Platform' })}
        </h1>
        <p className="text-muted-foreground mt-1">
          {t('developer.subtitle', {
            defaultValue: 'API access, webhooks and integrations for connecting KaziPeople to your other systems.',
          })}
        </p>
      </div>

      <RetryScope>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="clients">
            <KeyRound className="w-4 h-4 mr-1.5" />
            {t('developer.tab_clients', { defaultValue: 'API clients' })}
          </TabsTrigger>
          <TabsTrigger value="applications">
            <AppWindow className="w-4 h-4 mr-1.5" />
            {t('developer.tab_applications', { defaultValue: 'Applications' })}
          </TabsTrigger>
          <TabsTrigger value="usage">
            <BarChart3 className="w-4 h-4 mr-1.5" />
            {t('developer.tab_usage', { defaultValue: 'Usage' })}
          </TabsTrigger>
          <TabsTrigger value="webhooks">
            <Webhook className="w-4 h-4 mr-1.5" />
            {t('developer.tab_webhooks', { defaultValue: 'Webhooks' })}
          </TabsTrigger>
          <TabsTrigger value="integrations">
            <Plug className="w-4 h-4 mr-1.5" />
            {t('developer.tab_integrations', { defaultValue: 'Integrations' })}
          </TabsTrigger>
          <TabsTrigger value="marketplace">
            <Store className="w-4 h-4 mr-1.5" />
            {t('developer.tab_marketplace', { defaultValue: 'Marketplace' })}
          </TabsTrigger>
          <TabsTrigger value="mcp">
            <Bot className="w-4 h-4 mr-1.5" />
            {t('developer.tab_mcp', { defaultValue: 'MCP' })}
          </TabsTrigger>
          <TabsTrigger value="cli">
            <Terminal className="w-4 h-4 mr-1.5" />
            {t('developer.tab_cli', { defaultValue: 'CLI' })}
          </TabsTrigger>
        </TabsList>
        <TabsContent value="clients" className="mt-4">
          <ApiClientsTab />
        </TabsContent>
        <TabsContent value="applications" className="mt-4">
          <ApplicationsTab />
        </TabsContent>
        <TabsContent value="usage" className="mt-4">
          <UsageTab />
        </TabsContent>
        <TabsContent value="webhooks" className="mt-4">
          <WebhooksTab />
        </TabsContent>
        <TabsContent value="integrations" className="mt-4">
          <IntegrationsTab />
        </TabsContent>
        <TabsContent value="marketplace" className="mt-4">
          <MarketplaceTab />
        </TabsContent>
        <TabsContent value="mcp" className="mt-4">
          <McpTab />
        </TabsContent>
        <TabsContent value="cli" className="mt-4">
          <CliTab />
        </TabsContent>
      </Tabs>
      </RetryScope>
    </div>
  );
};

export default DeveloperPage;
